import * as React from 'react';
import { IEveBuyboxData } from '../eve-buybox.data';
import { IEveBuyboxResources, IEveBuyboxProps } from '../eve-buybox.props.autogenerated';

import { IBuyboxProductQuantityViewProps } from '@msdyn365-commerce-modules/buybox';

import { ITelemetryContent, Node } from '@msdyn365-commerce-modules/utilities';
import { IEvebuyboxCallbacks, IEvebuyboxState } from '../utilities/eve-buyboxInterface';
import { IEveBuyboxViewProps } from '../eve-buybox';

interface IBuyboxQuantitySelectorState {
    quantityComponent: IBuyboxProductQuantityViewProps;
    callbacks: IEvebuyboxCallbacks;
    props: IEveBuyboxViewProps & IEveBuyboxProps<IEveBuyboxData>;
    state: IEvebuyboxState;
    extentionResources: IEveBuyboxResources;
    quantityLimitsMessages: React.ReactNode;

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from EveBuybox.tsx file
    max: number | undefined;
    telemetryContent?: ITelemetryContent;
    unitOfMeasure?: React.ReactNode;
    onCaseChangeHandler: (callbacks: IEvebuyboxCallbacks) => (newValue: number) => boolean;
    onEachChangeHandler: (callbacks: IEvebuyboxCallbacks) => (newValue: number) => boolean;
}

export const BuyboxQuantitySelectors: React.FC<IBuyboxQuantitySelectorState> = ({
    quantityComponent,
    callbacks,
    props,
    state,
    extentionResources,
    quantityLimitsMessages,
    max,
    telemetryContent,
    unitOfMeasure,
    onCaseChangeHandler,
    onEachChangeHandler
}) => {
    //const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources, quantity } = props;

    const handleCaseQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        callbacks.updateCaseQuantity(e.target.valueAsNumber);
    };

    const incrementCaseQuantity = () => {
        if (state.caseQuantity + 1 <= (max ?? 15)) {
            callbacks.updateCaseQuantity(state.caseQuantity + 1);
        }
    };

    const decrementCaseQuantity = () => {
        if (state.caseQuantity !== 0) {
            callbacks.updateCaseQuantity(state.caseQuantity - 1); // only update if it's 0 or greater
        }
    };

    const renderCaseQuantity = (
        quantityComponent: IBuyboxProductQuantityViewProps,
        callbacks: IEvebuyboxCallbacks,
        props: IEveBuyboxProps<IEveBuyboxData>,
        state: IEvebuyboxState,
        extentionResources: IEveBuyboxResources,
        quantityLimitsMessages: React.ReactNode,

        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from EveBuybox.tsx file
        max: number | undefined,
        telemetryContent?: ITelemetryContent,
        unitOfMeasure?: React.ReactNode
    ): JSX.Element => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from EveBuybox.tsx file
        const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

        //const { resources } = props;

        const { caseQuantity } = state;

        return (
            <Node {...ContainerProps}>
                <Node {...LabelContainerProps}>
                    {heading}
                    {errors}
                </Node>
                <div className='quantity'>
                    <button type={'button'} className={'decrement quantity__controls'} onClick={decrementCaseQuantity}></button>
                    <input
                        id={'case-quantity-input'}
                        className='quantity-input'
                        type='number'
                        min={'0'}
                        max={max}
                        value={caseQuantity}
                        aria-label={'Change Quantity'}
                        onChange={handleCaseQuantityChange}
                    />
                    <button type={'button'} className={'increment quantity__controls'} onClick={incrementCaseQuantity}></button>
                </div>
                <div className='ms-buybox__product-unit-of-measure'>
                    <span>{unitOfMeasure}</span>
                </div>
                {quantityLimitsMessages}
            </Node>
        );
    };

    const handleEachQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let remainder = e.target.valueAsNumber;
        let cases = state.caseQuantity;
        while (remainder >= state.eachFactor) {
            remainder -= state.eachFactor;
            cases++;
        }
        if (cases >= (max ?? 15)) {
            cases = max ?? 15;
            remainder = state.eachFactor - 1;
        }
        callbacks.updateEachQuantity(remainder);
        callbacks.updateCaseQuantity(cases);
    };

    const incrementEachQuantity = () => {
        let remainder = state.eachQuantity + 1; //Magic; but we are incrementing.
        let cases = state.caseQuantity;
        while (remainder >= state.eachFactor) {
            remainder -= state.eachFactor;
            cases++;
        }

        if (cases >= (max ?? 15)) {
            cases = max ?? 15;
            remainder = state.eachFactor - 1;
        }

        callbacks.updateEachQuantity(remainder);
        callbacks.updateCaseQuantity(cases);
    };

    const decrementEachQuantity = () => {
        const remainder = state.eachQuantity - 1; //Magic; but we are decrementing.
        if (remainder >= 0) {
            callbacks.updateEachQuantity(remainder); // only update if it's 0 or greater
        }
    };

    /**
     * renderEachQuantity.
     * @param quantityComponent - QuantityComponent.
     * @param callbacks - Callbacks.
     * @param props - Props.
     * @param state - State.
     * @param extentionResources - ExtentionResources.
     * @param quantityLimitsMessages - QuantityLimitsMessages.
     * @param max - Max.
     * @param telemetryContent - TelemetryContent.
     * @param unitOfMeasure - Unit Of Measure.
     * @returns -- Returns.
     */
    const renderEachQuantity = (
        quantityComponent: IBuyboxProductQuantityViewProps,
        callbacks: IEvebuyboxCallbacks,
        props: IEveBuyboxProps<IEveBuyboxData>,
        state: IEvebuyboxState,
        extentionResources: IEveBuyboxResources,
        quantityLimitsMessages: React.ReactNode,

        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from EveBuybox.tsx file
        max: number | undefined,
        eaFactor: number,
        telemetryContent?: ITelemetryContent
    ): JSX.Element => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from EveBuybox.tsx file
        const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

        // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
        const product = props.data.product?.result;
        if (!product || !product.DefaultUnitOfMeasure) {
            props.telemetry.warning('Missing defult unit of measureor theproduct');
        }

        const { eachQuantity } = state;
        if (eaFactor !== -1 && product?.DefaultUnitOfMeasure?.toLowerCase() !== 'ea') {
            return (
                <Node {...ContainerProps}>
                    <Node {...LabelContainerProps}>
                        {heading}
                        {errors}
                    </Node>

                    <div className='quantity'>
                        <button type={'button'} className={'decrement quantity__controls'} onClick={decrementEachQuantity}></button>
                        <input
                            id={'each-quantity-input'}
                            className='quantity-input'
                            type='number'
                            min={'0'}
                            max={eaFactor}
                            value={eachQuantity}
                            aria-label={'Change Quantity'}
                            onChange={handleEachQuantityChange}
                        />
                        <button type={'button'} className={'increment quantity__controls'} onClick={incrementEachQuantity}></button>
                    </div>
                    <div className='ms-buybox__product-unit-of-measure'>
                        <span>EA</span>
                    </div>
                    {quantityLimitsMessages}
                </Node>
            );
        }
        return <></>;
    };

    return (
        <div className='msc-buybox__bulk-purchase-section'>
            <div className='msc-buybox__bulk-purchase-button-text'>
                {quantity &&
                    renderCaseQuantity(
                        quantity,
                        callbacks,
                        props,
                        state,
                        resources,
                        quantityLimitsMessages,
                        max,
                        telemetryContent,
                        unitOfMeasure
                    )}
                {quantity &&
                    renderEachQuantity(
                        quantity,
                        callbacks,
                        props,
                        state,
                        resources,
                        quantityLimitsMessages,
                        max,
                        state.eachFactor,
                        telemetryContent
                    )}
                {props.bulkPurchaseLink}
            </div>
        </div>
    );
};
